<template>
  <div>
    <SeedJung_Header />
    
    <div class="review-container">
      <div class="review-intro">
        <p class="intro-message">
          후기는 단순히 시드정에 대한 신뢰를 얻고자 하는 공간이 아닙니다.
          서로의 느낀점을 공유하는 과정에서 소중한 가치를 얻어가실 수 있습니다.
        </p>
      </div>

      <div v-if="!isWriting">
        <!-- 상단 헤더 영역 -->
        <div class="page-header">
          <h2 class="page-title">상담 후기</h2>
          <button class="write-button" @click="showForm">후기 등록</button>
        </div>

        <!-- 데스크톱 뷰 -->
        <table class="desktop-view">
          <thead>
            <tr>
              <th style="width: 8%; text-align: center;">번호</th>
              <th style="width: 60%; text-align: center;">제목</th>
              <th style="width: 12%; text-align: center;">작성자</th>
              <th style="width: 20%; text-align: center;">작성일</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(review, index) in paginatedReviews" :key="review.id">
              <tr class="title-row" @click="toggleReview(review.id)">
                <td style="text-align: center;">{{ calculateNumber(index) }}</td>
                <td style="padding-left: 20px;">
                  {{ review.title }}
                  <span class="toggle-icon">{{ review.id === openReviewId ? '▼' : '▶' }}</span>
                </td>
                <td style="text-align: center;">{{ review.name }}</td>
                <td style="text-align: center;">{{ review.date }}</td>
              </tr>
              <tr v-show="review.id === openReviewId" class="content-row">
                <td colspan="4" class="review-content">
                  {{ review.text }}
                  <div v-if="isMyReview(review)" class="review-actions">
                    <button @click.stop="deleteReview(review.id)" class="delete-button">삭제</button>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <!-- 모바일 뷰 -->
        <div class="mobile-view">
          <div v-for="review in paginatedReviews" :key="review.id" class="mobile-item">
            <div class="mobile-title" @click="toggleReview(review.id)">
              <span>{{ review.title }}</span>
              <span class="toggle-icon">{{ review.id === openReviewId ? '▼' : '▶' }}</span>
            </div>
            <div class="mobile-info">
              <span class="mobile-author">{{ review.name }}</span>
              <span class="mobile-date">{{ review.date }}</span>
            </div>
            <div v-show="review.id === openReviewId" class="mobile-content">
              {{ review.text }}
              <div v-if="isMyReview(review)" class="review-actions">
                <button @click.stop="deleteReview(review.id)" class="delete-button">삭제</button>
              </div>
            </div>
          </div>
        </div>

        <div class="pagination">
          <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1">이전</button>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">다음</button>
        </div>
        
        <!-- 검색 영역 -->
        <div class="search-wrapper">
          <div class="search-bar">
            <select v-model="searchType" class="search-type">
              <option value="title">제목</option>
              <option value="author">작성자</option>
            </select>
            <input 
              v-model="searchTerm" 
              :placeholder="searchPlaceholder"
              @keyup.enter="handleSearch"
              class="search-input"
            >
            <button 
              @click="handleSearch" 
              class="search-button"
            >
              검색
            </button>
          </div>
        </div>        
      </div>

      <!-- 리뷰 작성 폼 -->
      <div v-else class="write-form">
        <div class="write-header">
          <h3>상담 후기 작성</h3>
          <button class="back-button" @click="isWriting = false">목록으로</button>
        </div>
        <div class="form-content">
          <div class="input-group">
            <label>닉네임</label>
            <input 
              v-model="reviewerName"
              placeholder="닉네임을 입력해주세요"
              class="name-input"
            />
          </div>
          <div class="input-group">
            <label>제목</label>
            <input 
              v-model="reviewTitle"
              placeholder="제목을 입력해주세요"
              class="title-input"
            />
          </div>
          <div class="input-group">
            <label>후기 내용</label>
            <textarea 
              v-model="reviewText"
              placeholder="상담 후기를 자유롭게 작성해주세요"
              rows="5"
            ></textarea>
          </div>
          <div class="button-group">
            <button class="cancel-button" @click="isWriting = false">취소</button>
            <button class="submit-button" @click="submitReview">등록하기</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SeedJung_Header from './SeedJung_Header.vue'
import '@/assets/css/table-style.css'
import axios from 'axios'

export default {
  name: 'SeedJung_Review',
  components: {
    SeedJung_Header
  },
  data() {
    return {
      isWriting: false,
      searchType: 'title',
      searchTerm: '',
      reviewerName: this.$store.getters.currentUser ? this.$store.getters.currentUser.nickname : '',
      currentSearchTerm: '',
      reviewTitle: '',
      reviewText: '',
      currentPage: 1,
      openReviewId: null,
      isMobile: false,
      originalReviews: [],
      totalItems: 0
    }
  },
  computed: {
    searchPlaceholder() {
      switch(this.searchType) {
        case 'title':
          return '제목을 입력하세요';
        case 'author':
          return '작성자를 입력하세요';
        default:
          return '검색어를 입력하세요';
      }
    },
    reviews() {
      if (!this.currentSearchTerm) {
        return this.originalReviews;
      }
      
      const searchLower = this.currentSearchTerm.toLowerCase();
      
      return this.originalReviews.filter(review => {
        switch(this.searchType) {
          case 'title':
            return review.title.toLowerCase().includes(searchLower);
          case 'author':
            return review.name.toLowerCase().includes(searchLower);
          default:
            return review.title.toLowerCase().includes(searchLower) ||
                   review.name.toLowerCase().includes(searchLower);
        }
      });
    },
    itemsPerPage() {
      return this.isMobile ? 5 : 10;
    },
    paginatedReviews() {
      return this.reviews;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
    }
  },
  created() {
    this.checkMobile();
    window.addEventListener('resize', this.handleResize);
    this.fetchReviews();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    async fetchReviews() {
      try {
        const response = await axios.get('/api/reviews', {
          params: {
            page: this.currentPage,
            itemsPerPage: this.itemsPerPage
          }
        });
        // reviews와 totalItems 모두 업데이트
        this.originalReviews = response.data.reviews;
        this.totalItems = response.data.totalItems; // totalItems를 실제로 사용
        this.currentPage = response.data.currentPage || this.currentPage; // 서버에서 현재 페이지도 받아오면 좋음
      } catch (error) {
        console.error('리뷰 목록 조회 실패:', error);
      }
    },
    showForm() {
      if (!this.$store.getters.currentUser) {
        alert('후기 등록은 로그인이 필요합니다.');
        this.$router.push('/login');
        return;
      }
      this.isWriting = true;
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    handleSearch() {
      this.currentSearchTerm = this.searchTerm;
      this.currentPage = 1;
    },
    handleResize() {
      this.checkMobile();
      this.currentPage = 1;
    },
    calculateNumber(index) {
      // 전체 아이템 수에서 현재 페이지와 인덱스를 고려하여 번호 계산
      return this.totalItems - ((this.currentPage - 1) * this.itemsPerPage + index);
    },
    async changePage(newPage) {
      if (newPage > 0 && newPage <= this.totalPages) {
        this.currentPage = newPage;
        await this.fetchReviews(); // 페이지 변경시 서버에서 해당 페이지 데이터 새로 요청
      }
    },
    toggleReview(id) {
      this.openReviewId = this.openReviewId === id ? null : id;
    },
    isMyReview(review) {
      const currentUser = this.$store.getters.currentUser?.username;
      // 관리자이거나 작성자 본인인 경우 true 반환
      return currentUser === 'alscofld95' || currentUser === review.username;
    },
    async deleteReview(id) {
      if (!confirm('정말 삭제하시겠습니까?')) return;

      try {
        const username = this.$store.getters.currentUser?.username;
        const response = await axios.delete(`/api/reviews/${id}`, {
          params: { username }
        });
        
        // 성공 메시지 표시
        alert(response.data.message); // "리뷰가 삭제되었습니다." 메시지 표시
        
        await this.fetchReviews();  // 목록 새로고침
        this.openReviewId = null;   // 열려있는 리뷰 닫기
      } catch (error) {
        if (error.response?.status === 403) {
          alert('삭제 권한이 없습니다.');
        } else {
          alert('상담후기 삭제에 실패했습니다.');
          console.error('리뷰 삭제 실패:', error);
        }
      }
    },
    async submitReview() {
      if (!this.reviewerName.trim() || !this.reviewTitle.trim() || !this.reviewText.trim()) {
        alert('모든 필드를 입력해주세요.');
        return;
      }

      const reviewData = {
        title: this.reviewTitle,
        author: this.reviewerName,
        content: this.reviewText,
        username: this.$store.getters.currentUser?.username
      };

      try {
        const response = await axios.post('/api/reviews', reviewData);
        alert(response.data.message);
        this.resetForm();
        this.fetchReviews();
      } catch (error) {
        alert('리뷰 등록에 실패했습니다.');
        console.error('리뷰 등록 실패:', error);
      }
    },
    resetForm() {
      this.reviewerName = '';
      this.reviewTitle = '';
      this.reviewText = '';
      this.isWriting = false;
    }
  }
}
</script>

<style scoped>
.review-container {
 max-width: 1200px;
 margin: 0 auto;
 padding: 20px;
 padding-top: 220px;
}

.review-intro {
 text-align: center;
 margin-bottom: 40px;
}

.intro-message {
 color: #666;
 line-height: 1.6;
 font-size: 1.1em;
 padding: 20px;
 background-color: #f8f9fa;
 border-radius: 8px;
}

.page-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 margin-bottom: 30px;
 padding-bottom: 15px;
 border-bottom: 2px solid #072c53;
}

.page-title {
 font-size: 24px;
 color: #072c53;
 font-weight: 600;
 margin: 0;
}

.write-button {
 background: #072c53;
 color: white;
 border: none;
 padding: 8px 16px;
 border-radius: 4px;
 cursor: pointer;
 font-size: 14px;
 white-space: nowrap;
 min-width: 90px;
}

.write-form {
 background: white;
 padding: 30px;
 border-radius: 8px;
 box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}

.write-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 margin-bottom: 20px;
}

.form-content {
 display: flex;
 flex-direction: column;
 gap: 20px;
}

.input-group label {
 display: block;
 margin-bottom: 8px;
 font-weight: 500;
}

.name-input, .title-input {
 width: 100%;
 max-width: 300px;
 padding: 8px;
 border: 1px solid #ddd;
 border-radius: 4px;
}

textarea {
 width: 100%;
 padding: 8px;
 border: 1px solid #ddd;
 border-radius: 4px;
 resize: vertical;
 min-height: 150px;
}

.button-group {
 display: flex;
 justify-content: flex-end;
 gap: 10px;
}

.back-button, .cancel-button {
 background: #f8f9fa;
 color: #333;
 border: 1px solid #ddd;
 padding: 8px 20px;
 border-radius: 4px;
 cursor: pointer;
}

.submit-button {
 background: #072c53;
 color: white;
 border: none;
 padding: 8px 20px;
 border-radius: 4px;
 cursor: pointer;
}

.review-actions {
 display: flex;
 justify-content: flex-end;
 margin-top: 15px;
 padding-top: 15px;
 border-top: 1px solid #eee;
}

.delete-button {
 padding: 6px 16px;
 background-color: white;
 color: #072c53;
 border: 1px solid #072c53;
 border-radius: 4px;
 cursor: pointer;
 font-size: 13px;
 transition: all 0.2s ease;
}

.delete-button:hover {
 background-color: #072c53;
 color: white;
}

.search-wrapper {
 background: #f8f9fa;
 width: 90%;
 margin: 20px auto 0;
 border-radius: 8px;
 display: flex;
 justify-content: center;
 padding: 15px 0;
}

.search-bar {
 display: flex;
 gap: 8px;
 width: 100%;
 max-width: 400px;
 margin: 0 auto;
}

.search-type {
 width: 90px;
 padding: 8px 10px;
 border: 1px solid #ddd;
 border-radius: 4px;
 background: white;
 cursor: pointer;
 font-size: 14px;
}

.search-input {
 width: 250px;
 padding: 8px 12px;
 border: 1px solid #ddd;
 border-radius: 4px;
 font-size: 14px;
}

.search-button {
 background: #072c53;
 color: white;
 border: none;
 padding: 8px 16px;
 border-radius: 4px;
 cursor: pointer;
 font-size: 14px;
 white-space: nowrap;
}

.pagination {
 display: flex;
 justify-content: center;
 align-items: center;
 margin-top: 20px;
}

.pagination button {
 margin: 0 10px;
 padding: 5px 10px;
 background-color: #072c53;
 color: white;
 border: none;
 border-radius: 4px;
 cursor: pointer;
}

.pagination button:disabled {
 background-color: #ddd;
 cursor: not-allowed;
}

@media (max-width: 768px) {
 .review-container {
   padding-top: 120px;
   padding-left: 15px;
   padding-right: 15px;
 }

 .page-title {
   font-size: 20px;
 }

 .write-form {
   padding: 20px;
 }

 .name-input, .title-input {
   max-width: none;
 }

 .desktop-view {
   display: none;
 }

 .mobile-view {
   display: block;
 }

 .mobile-item {
   border-bottom: 1px solid #ddd;
   padding: 15px 0;
 }

 .mobile-info {
   display: flex;
   justify-content: space-between;
   font-size: 12px;
   color: #666;
   margin-top: 5px;
 }

 .mobile-content {
   padding: 15px;
   background-color: #f8f9fa;
   margin-top: 10px;
   border-radius: 4px;
   white-space: pre-line;
 }

 .search-wrapper {
   margin: 20px 15px;
   width: auto;
 }

 .search-bar {
   width: 100%;
 }

 .search-type {
   width: 80px;
   min-width: 80px;
 }

 .search-input {
   flex: 1;
   min-width: 0;
 }

 .search-button {
   min-width: 60px;
 }
}
</style>